import React, { useState, useEffect, useRef } from 'react';
import { 
  MagnifyingGlassIcon, 
  SparklesIcon,
  ClockIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const SmartSearchBar = ({ onSearch, onProductSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Load recent searches from localStorage
    const recent = JSON.parse(localStorage.getItem('recentB2BSearches') || '[]');
    setRecentSearches(recent.slice(0, 5));
  }, []);

  useEffect(() => {
    // Click outside to close suggestions
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/api/products/quick-search?q=${encodeURIComponent(query)}&limit=8`);
      setSuggestions(response.data.results || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setShowSuggestions(true);

    // Debounce API calls
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSelectSuggestion = (product) => {
    setSearchTerm(product.name);
    setShowSuggestions(false);
    
    // Save to recent searches
    saveRecentSearch(product.name);
    
    if (onProductSelect) {
      onProductSelect(product);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      saveRecentSearch(searchTerm);
      onSearch(searchTerm);
      setShowSuggestions(false);
    }
  };

  const saveRecentSearch = (term) => {
    const recent = JSON.parse(localStorage.getItem('recentB2BSearches') || '[]');
    const updated = [term, ...recent.filter(r => r !== term)].slice(0, 10);
    localStorage.setItem('recentB2BSearches', JSON.stringify(updated));
    setRecentSearches(updated.slice(0, 5));
  };

  const handleRecentClick = (term) => {
    setSearchTerm(term);
    onSearch(term);
    setShowSuggestions(false);
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      regex.test(part) ? 
        <span key={i} className="font-semibold text-blue-600">{part}</span> : 
        part
    );
  };

  return (
    <div ref={searchRef} className="relative flex-1 min-w-64 max-w-2xl">
      <form onSubmit={handleSearch} className="relative">
        <div className="relative">
          <SparklesIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-purple-400" />
          <input
            type="text"
            placeholder="חיפוש חכם: שם, SKU, תיאור, או הדבק רשימת SKU..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            className="w-full pr-10 pl-10 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
          />
          {loading && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
            </div>
          )}
          {!loading && (
            <button 
              type="submit"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-600"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && (searchTerm || recentSearches.length > 0) && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 max-h-96 overflow-y-auto">
          {/* AI Suggestions */}
          {suggestions.length > 0 && (
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 flex items-center">
                <SparklesIcon className="h-4 w-4 ml-1 text-purple-500" />
                הצעות חכמות
              </div>
              {suggestions.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleSelectSuggestion(product)}
                  className="w-full px-3 py-3 hover:bg-purple-50 rounded-lg flex items-center gap-3 text-right transition-colors"
                >
                  {product.image && (
                    <img 
                      src={`/api${product.image}`}
                      alt={product.name}
                      className="w-10 h-10 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {highlightMatch(product.name, searchTerm)}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <span className="font-mono">{product.sku}</span>
                      {product.price && (
                        <span className="font-semibold text-green-600">
                          ₪{product.price}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Recent Searches */}
          {!searchTerm && recentSearches.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 flex items-center">
                <ClockIcon className="h-4 w-4 ml-1 text-gray-400" />
                חיפושים אחרונים
              </div>
              {recentSearches.map((term, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentClick(term)}
                  className="w-full px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-2 text-right text-sm text-gray-700 transition-colors"
                >
                  <FireIcon className="h-4 w-4 text-orange-400" />
                  {term}
                </button>
              ))}
            </div>
          )}

          {/* No results */}
          {searchTerm && !loading && suggestions.length === 0 && (
            <div className="p-4 text-center text-gray-500 text-sm">
              לא נמצאו תוצאות עבור "{searchTerm}"
              <div className="mt-2 text-xs text-gray-400">
                נסה מילות חיפוש אחרות או SKU
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartSearchBar;

